import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    console.log('[media-gallery] handleVariantUpdate called', {
      hasHtml: !!event.detail.data.html,
      hasNewProduct: !!event.detail.data.newProduct,
      newProductId: event.detail.data.newProduct?.id,
    });

    const source = event.detail.data.html;

    if (!source) {
      console.warn('[media-gallery] No HTML in event data!');
      return;
    }

    // Check current gallery content
    const currentImages = this.querySelectorAll('img');
    const currentVideos = this.querySelectorAll('video');
    const currentMediaRef = this.querySelector('[ref="media"]');
    const hasCurrentContent = currentImages.length > 0 || currentVideos.length > 0 || currentMediaRef !== null;
    console.log('[media-gallery] Current gallery content:', {
      hasContent: hasCurrentContent,
      imageCount: currentImages.length,
      videoCount: currentVideos.length,
      hasMediaRef: !!currentMediaRef,
      firstImageSrc: currentImages[0]?.src,
    });

    // Check DOM gallery (what morph updated)
    const domGallery = document.querySelector('media-gallery');
    const domImages = domGallery ? domGallery.querySelectorAll('img') : [];
    const domVideos = domGallery ? domGallery.querySelectorAll('video') : [];
    const domHasContent = domImages.length > 0 || domVideos.length > 0 || (domGallery?.querySelector('[ref="media"]') !== null);
    console.log('[media-gallery] Gallery from current DOM:', {
      exists: !!domGallery,
      isSame: domGallery === this,
      hasContent: domHasContent,
      imageCount: domImages.length,
      videoCount: domVideos.length,
      firstImageSrc: domImages[0]?.src,
    });

    // Try event HTML
    const newMediaGallery = source.querySelector('media-gallery');
    const eventImages = newMediaGallery ? newMediaGallery.querySelectorAll('img') : [];
    const eventVideos = newMediaGallery ? newMediaGallery.querySelectorAll('video') : [];
    const eventHasContent = eventImages.length > 0 || eventVideos.length > 0 || (newMediaGallery?.querySelector('[ref="media"]') !== null);
    console.log('[media-gallery] Gallery from event HTML:', {
      exists: !!newMediaGallery,
      hasContent: eventHasContent,
      imageCount: eventImages.length,
      videoCount: eventVideos.length,
      firstImageSrc: eventImages[0]?.src,
    });

    // Priority: 1. DOM gallery (from morph), 2. Event HTML gallery, 3. Keep current if it has content
    let galleryToUse = null;
    let gallerySource = 'none';

    // Check DOM gallery first (this is what morph updated)
    if (domGallery && domGallery !== this && domHasContent) {
      galleryToUse = domGallery;
      gallerySource = 'DOM';
      console.log('[media-gallery] Using gallery from DOM (morphed)');
    }
    // If DOM gallery not available, try event HTML
    else if (newMediaGallery && eventHasContent) {
      galleryToUse = newMediaGallery;
      gallerySource = 'event HTML';
      console.log('[media-gallery] Using gallery from event HTML');
    }
    // If no valid gallery found, preserve current if it has content
    else if (hasCurrentContent) {
      console.log('[media-gallery] Preserving current gallery - no valid new gallery found');
      return;
    } else {
      console.warn('[media-gallery] No gallery found anywhere! Current:', hasCurrentContent, 'DOM:', domHasContent, 'Event:', eventHasContent);
      return;
    }

    // Use morph instead of replaceWith to properly handle component lifecycle
    // This prevents errors when child components (zoom-dialog, slideshow) try to clean up
    // Wait a frame to ensure any pending operations complete
    console.log('[media-gallery] Morphing gallery from', gallerySource);
    requestAnimationFrame(() => {
      try {
        morph(this, galleryToUse);
        console.log('[media-gallery] Gallery morphed successfully');
        // Check if images are visible after morph
        setTimeout(() => {
          const imagesAfterMorph = this.querySelectorAll('img');
          console.log('[media-gallery] After morph check:', {
            imageCount: imagesAfterMorph.length,
            firstImageSrc: imagesAfterMorph[0]?.src,
            firstImageVisible: imagesAfterMorph[0]?.offsetParent !== null,
            firstImageComplete: imagesAfterMorph[0]?.complete,
          });
        }, 100);
      } catch (error) {
        console.error('[media-gallery] Error during morph:', error);
      }
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
